import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CardFlags } from '../../types/card-flags.enum';

import { useLocalStorage } from 'usehooks-ts';
import { SumDaysFromNow } from '../../utils/SumDaysFromNow';
import { getNextBusinessDay } from '../../utils/getNextBusinessDay';

interface FiltersContextType {
  periodType: string;
  setPeriodType: (periodType: string) => void;
  initialDate: Date;
  setInitialDate: React.Dispatch<React.SetStateAction<Date>>;
  finalDate: Date;
  setFinalDate: React.Dispatch<React.SetStateAction<Date>>;
  situation: string;
  setSituation: (situation: string) => void;
  productType: string;
  setProductType: (productType: string) => void;
  flag: string;
  setFlag: (flag: string) => void;
  agroup: string;
  setAgroup: (agroup: string) => void;
  extractInitialDate: Date;
  setExtractInitialDate: React.Dispatch<React.SetStateAction<Date>>;
  extractFinalDate: Date;
  setExtractFinalDate: React.Dispatch<React.SetStateAction<Date>>;
  desiredValue: number;
  setDesiredValue: (desired: number) => void;
  anticipationDate: Date;
  setAnticipationDate: React.Dispatch<React.SetStateAction<Date>>;
  cardFlagsOptions: {
    id: string;
    value: string;
  }[];
}

interface FiltersContextProviderProps {
  children: ReactNode;
}

const FiltersContext = createContext({} as FiltersContextType);

export function FiltersContextProvider({
  children,
}: FiltersContextProviderProps) {
  const today = new Date();
  const thirtyDaysAgo = new Date().setDate(today.getDate() - 30);
  const daysOfWeek = new Date().setDate(today.getDate() - 7);

  const [periodType, setPeriodType] = useState('');
  const [initialDate, setInitialDate] = useState(new Date(thirtyDaysAgo));
  const [finalDate, setFinalDate] = useState(today);
  const [situation, setSituation] = useState('');
  const [productType, setProductType] = useState('');
  const [flag, setFlag] = useState('');
  const [agroup, setAgroup] = useState('');

  const [extractInitialDate, setExtractInitialDate] = useState(
    new Date(daysOfWeek)
  );
  const [extractFinalDate, setExtractFinalDate] = useState(today);
  const [anticipationDate, setAnticipationDate] = useState(
    getNextBusinessDay(SumDaysFromNow(today, 1))
  );
  const [desiredValue, setDesiredValue] = useState(0);

  const cardFlagsOptions = Object.keys(CardFlags)?.map((flag) => ({
    id: flag,
    value: flag.charAt(0) + flag.slice(1).toLowerCase(),
  }));

  const storageItems = useMemo(
    () => ({
      periodType,
      initialDate,
      finalDate,
      extractInitialDate,
      extractFinalDate,
      situation,
      productType,
      flag,
      agroup,
      desiredValue,
      anticipationDate,
    }),
    [
      finalDate,
      flag,
      agroup,
      initialDate,
      periodType,
      productType,
      situation,
      extractInitialDate,
      extractFinalDate,
      desiredValue,
      anticipationDate,
    ]
  );

  const [filtersStorage, setFiltersStorage] = useLocalStorage(
    '@zignetPortalEstabelecimentos:filters',
    storageItems
  );

  useEffect(() => {
    setFiltersStorage(storageItems);
  }, [setFiltersStorage, storageItems]);

  useEffect(() => {
    setPeriodType(filtersStorage.periodType);
    setInitialDate(new Date(filtersStorage.initialDate));
    setFinalDate(new Date(filtersStorage.finalDate));
    setSituation(filtersStorage.situation);
    setProductType(filtersStorage.productType);
    setFlag(filtersStorage.flag);
    setAgroup(filtersStorage.agroup);
    setExtractInitialDate(new Date(filtersStorage.extractInitialDate));
    setExtractFinalDate(new Date(filtersStorage.extractFinalDate));
    setDesiredValue(filtersStorage.desiredValue);
  }, []);

  return (
    <FiltersContext.Provider
      value={{
        periodType,
        setPeriodType,
        initialDate,
        setInitialDate,
        finalDate,
        setFinalDate,
        situation,
        setSituation,
        productType,
        setProductType,
        flag,
        setFlag,
        cardFlagsOptions,
        agroup,
        setAgroup,
        extractInitialDate,
        setExtractInitialDate,
        extractFinalDate,
        setExtractFinalDate,
        desiredValue,
        setDesiredValue,
        anticipationDate,
        setAnticipationDate,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
}

const useFilters = () => {
  const context = useContext(FiltersContext);
  if (context === undefined) {
    throw new Error('useFilters can only be used inside FiltersContext');
  }

  return context;
};

export { FiltersContext, useFilters };
